import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Landmark,
  DollarSign,
  TrendingDown,
  TrendingUp,
  Gauge,
  CreditCard as CreditCardIcon,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  Camera,
  PiggyBank,
  Eye,
  EyeOff,
  Droplets,
  DropletOff,
} from 'lucide-react';
import useStore from '../../store/useStore';
import { Card, KPICard } from '../../components/ui/Card';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency, formatPercent, formatDate, formatNumber } from '../../utils/format';
import { computeYtdContributions } from '../../data/balanceSheetHistory';
import { BS_CATEGORY_OPTIONS, BS_CATEGORY_LABEL, inferBalanceSheetCategory } from '../../data/balanceSheetCategories';

// ─── Account type groupings ─────────────────────────────────────────────────
const CASH_TYPES = ['cash', 'checking', 'savings'];
const INVESTMENT_TYPES = ['investment'];
const PROPERTY_TYPES = ['property'];
const OTHER_TYPES = ['other'];
const LOAN_TYPES = ['loan'];

const INPUT_CLASS =
  'w-full bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent';

// ─── Blank form states ──────────────────────────────────────────────────────
const BLANK_ACCOUNT = { name: '', type: 'checking', balance: '', institution: '', statement: '', asOfDate: '', balanceSheetCategory: '' };
const BLANK_CREDIT_CARD = {
  name: '',
  lastFour: '',
  type: 'Visa',
  balance: '',
  creditLimit: '',
  annualFee: '',
  interestRate: '',
  pointsBalance: '',
  pointsType: '',
  paymentDueDate: '',
  lastClosingDate: '',
  nextClosingDate: '',
  statement: '',
  asOfDate: '',
};

// ─── Inline Edit Components ─────────────────────────────────────────────────

// Parse numeric input: strip commas, evaluate simple +/- expressions
function parseNumericInput(raw) {
  if (!raw && raw !== 0) return NaN;
  const s = String(raw).replace(/,/g, '').replace(/\s/g, '');
  if (!s) return NaN;
  // Only allow digits, decimal points, and basic math operators
  if (!/^[\d.+\-*/()]+$/.test(s)) return NaN;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function(`"use strict"; return (${s});`)();
    return typeof result === 'number' && isFinite(result) ? result : NaN;
  } catch { return NaN; }
}

function InlineEditCell({ value, displayValue, align = 'right', className = '', onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    const num = parseNumericInput(editVal);
    if (!isNaN(num) && num !== value) onSave(num);
    setEditing(false);
  };

  if (editing) {
    return (
      <Td align={align}>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
          }}
          className="w-36 bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary font-mono text-right focus:outline-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      align={align}
      className={`${className} cursor-pointer hover:bg-accent/10 transition-colors rounded`}
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit"
    >
      {displayValue}
    </Td>
  );
}

function InlineEditText({ value, displayValue, className = '', inputClassName = '', onSave }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const save = () => {
    const num = parseNumericInput(editVal);
    if (!isNaN(num) && num !== value) onSave(num);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={editVal}
        onChange={(e) => setEditVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.target.blur();
          if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
        }}
        className={`bg-bg-tertiary border border-accent rounded px-2 py-1 font-mono focus:outline-none ${inputClassName}`}
        autoFocus
      />
    );
  }

  return (
    <span
      className={`${className} cursor-pointer hover:bg-accent/10 rounded px-1 -mx-1 transition-colors`}
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function AccountSection({ title, accounts, allForTotal, onRemove, onUpdate, onToggleHidden, onToggleLiquidity, editableFilter, hiddenIds, liquidityExclusions, liquidityEligible }) {
  if (!accounts.length) return null;
  const total = (allForTotal || accounts).reduce((sum, a) => sum + a.balance, 0);

  return (
    <Card className="section-gap">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <span className="text-sm font-semibold text-text-primary">
          {formatCurrency(total)}
        </span>
      </div>
      <Table fixed>
        <Thead>
          <tr>
            <Th>Account</Th>
            <Th className="w-[140px]">BS Category</Th>
            <Th align="right" className="w-[140px]">Liquidity</Th>
            <Th align="right" className="w-[140px]">Balance</Th>
            <Th align="right" className="w-20" />
          </tr>
        </Thead>
        <tbody>
          {accounts.map((account) => {
            const canEdit = onUpdate && (!editableFilter || editableFilter(account));
            const isHidden = (hiddenIds || []).includes(account.id);
            const isLiquidityEligible = liquidityEligible ? liquidityEligible(account) : false;
            const isExcludedFromLiquidity = isLiquidityEligible && (liquidityExclusions || []).includes(account.id);
            const catKey = account.balanceSheetCategory || inferBalanceSheetCategory(account);
            const catLabel = catKey ? (BS_CATEGORY_LABEL[catKey] || catKey) : '—';
            return (
              <Tr key={account.id} className={isHidden ? 'opacity-50' : ''}>
                <Td>
                  <div>
                    <span className="text-text-primary">{account.name}</span>
                    {account.institution && (
                      <span className="text-text-muted text-xs ml-2">
                        {account.institution}
                      </span>
                    )}
                  </div>
                </Td>
                <Td>
                  <select
                    value={account.balanceSheetCategory || ''}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      if (onUpdate) onUpdate(account.id, { balanceSheetCategory: val });
                    }}
                    className="w-full bg-transparent border border-border/30 rounded px-1.5 py-1 text-xs text-text-primary focus:outline-none focus:border-accent cursor-pointer"
                  >
                    <option value="">{catKey ? `(Auto: ${catLabel})` : '(None)'}</option>
                    {BS_CATEGORY_OPTIONS.map((group) => (
                      <optgroup key={group.group} label={group.group}>
                        {group.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </Td>
                <Td align="right">
                  {isLiquidityEligible ? (
                    <div className="flex items-center justify-end gap-1">
                      <span className={`font-mono ${isExcludedFromLiquidity ? 'text-text-muted line-through' : 'text-text-secondary'}`}>
                        {(account.statement != null && account.statement !== 0)
                          ? formatCurrency(account.statement, 2)
                          : formatCurrency(account.balance, 2)}
                      </span>
                      {onToggleLiquidity && (
                        <button
                          onClick={() => onToggleLiquidity(account.id)}
                          className="p-1 rounded-md text-text-muted hover:text-accent hover:bg-accent/20 transition-colors"
                          title={isExcludedFromLiquidity ? 'Include in liquidity' : 'Exclude from liquidity'}
                        >
                          {isExcludedFromLiquidity
                            ? <DropletOff className="w-3.5 h-3.5" />
                            : <Droplets className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="font-mono text-text-muted">—</span>
                  )}
                </Td>
                {canEdit ? (
                  <InlineEditCell
                    value={account.balance}
                    displayValue={formatCurrency(account.balance, 2)}
                    className="font-mono text-text-primary"
                    onSave={(val) => onUpdate(account.id, { balance: val, statement: val })}
                  />
                ) : (
                  <Td align="right" className="font-mono text-text-primary">
                    {formatCurrency(account.balance, 2)}
                  </Td>
                )}
                <Td align="right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onToggleHidden(account.id)}
                      className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-accent/20 transition-colors"
                      title={isHidden ? 'Show account' : 'Hide account'}
                    >
                      {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => onRemove(account.id)}
                      className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/20 transition-colors"
                      title="Delete account"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

function UtilizationBar({ balance, limit }) {
  const absBalance = Math.abs(balance);
  const pct = limit > 0 ? (absBalance / limit) * 100 : 0;
  const barColor =
    pct > 50 ? 'bg-danger' : pct > 30 ? 'bg-warning' : 'bg-success';
  const textColor =
    pct > 50 ? 'text-danger' : pct > 30 ? 'text-warning' : 'text-success';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-medium ${textColor}`}>
          {pct.toFixed(1)}%
        </span>
        <span className="text-text-muted text-xs">
          {formatCurrency(absBalance)} / {formatCurrency(limit)}
        </span>
      </div>
      <div className="w-full h-2 bg-bg-tertiary rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function CreditCardTile({ card, onRemove, onUpdate, onToggleHidden, isHidden, accounts, creditCards, onPayStatement }) {
  const absBalance = Math.abs(card.balance);
  const stmtBalance = Math.abs(card.statement || 0);
  const utilization =
    card.creditLimit > 0 ? (absBalance / card.creditLimit) * 100 : 0;
  const [payMode, setPayMode] = useState(false);
  const [payAccountId, setPayAccountId] = useState('');

  const handlePayStatement = () => {
    if (!payAccountId || stmtBalance <= 0) return;
    const allSources = [
      ...(accounts || []).map((a) => ({ ...a, sourceType: 'account' })),
      ...(creditCards || []).filter((c) => c.id !== card.id).map((c) => ({ ...c, sourceType: 'creditCard' })),
    ];
    const source = allSources.find((a) => a.id === payAccountId);
    if (!source) return;
    onPayStatement(card, stmtBalance, source);
    setPayMode(false);
    setPayAccountId('');
  };

  return (
    <Card className="relative overflow-hidden">
      {/* Colored top accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{
          background:
            utilization > 50
              ? '#EF4444'
              : utilization > 30
              ? '#F59E0B'
              : '#10B981',
        }}
      />

      <div className="pt-2">
        {/* Card Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-text-primary font-semibold text-sm">
              {card.name}
            </h3>
            <p className="text-text-muted text-xs mt-0.5">
              {card.type} {'\u2022'} ****{card.lastFour}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {onToggleHidden && (
              <button
                onClick={() => onToggleHidden(card.id)}
                className="p-1.5 rounded-md text-text-muted hover:text-text-secondary transition-colors"
                title={isHidden ? 'Unhide card' : 'Hide card'}
              >
                {isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            )}
            <button
              onClick={() => onRemove(card.id)}
              className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/20 transition-colors"
              title="Delete credit card"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <CreditCardIcon className="w-5 h-5 text-text-secondary" />
          </div>
        </div>

        {/* Balance */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-0.5">
            <p className="text-text-secondary text-xs uppercase tracking-wider">
              Current Balance
            </p>
            {card.asOfDate && (
              <p className="text-text-muted text-[10px]">
                as of {formatDate(card.asOfDate)}
              </p>
            )}
          </div>
          {onUpdate ? (
            <InlineEditText
              value={absBalance}
              displayValue={formatCurrency(absBalance, 2)}
              className="text-2xl font-bold text-text-primary"
              inputClassName="w-full text-xl font-bold text-text-primary"
              onSave={(val) => onUpdate(card.id, { balance: -Math.abs(val) })}
            />
          ) : (
            <p className="text-2xl font-bold text-text-primary">
              {formatCurrency(absBalance, 2)}
            </p>
          )}
        </div>

        {/* Utilization Bar */}
        <div className="mb-4">
          <UtilizationBar balance={card.balance} limit={card.creditLimit} />
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="text-text-muted">Credit Limit</p>
            <p className="text-text-primary font-medium">
              {formatCurrency(card.creditLimit)}
            </p>
          </div>
          <div>
            <p className="text-text-muted">APR</p>
            <p className="text-text-primary font-medium">
              {card.interestRate > 0 ? `${card.interestRate}%` : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Points/Miles</p>
            <p className="text-text-primary font-medium">
              {formatNumber(card.pointsBalance)}{' '}
              <span className="text-text-muted">{card.pointsType}</span>
            </p>
          </div>
          <div>
            <p className="text-text-muted">Annual Fee</p>
            <p className="text-text-primary font-medium">
              {card.annualFee > 0 ? formatCurrency(card.annualFee) : '$0'}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Payment Due</p>
            <p className="text-text-primary font-medium">
              {card.paymentDueDate ? formatDate(card.paymentDueDate) : '--'}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Next Close</p>
            <p className="text-text-primary font-medium">
              {card.nextClosingDate ? formatDate(card.nextClosingDate) : '--'}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Last Close</p>
            <p className="text-text-primary font-medium">
              {card.lastClosingDate ? formatDate(card.lastClosingDate) : '--'}
            </p>
          </div>
          <div>
            <p className="text-text-muted">Statement</p>
            {onUpdate ? (
              <InlineEditText
                value={Math.abs(card.statement || 0)}
                displayValue={
                  card.statement != null && card.statement !== 0
                    ? formatCurrency(Math.abs(card.statement), 2)
                    : '$0'
                }
                className="text-text-primary font-medium"
                inputClassName="w-full text-xs text-text-primary"
                onSave={(val) => onUpdate(card.id, { statement: -Math.abs(val) })}
              />
            ) : (
              <p className="text-text-primary font-medium">
                {card.statement != null && card.statement !== 0
                  ? formatCurrency(Math.abs(card.statement), 2)
                  : '$0'}
              </p>
            )}
          </div>
        </div>

        {/* Daily Spend metric */}
        {card.dailySpend != null && card.dailySpend !== 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-muted">Avg Daily Spend</span>
              <span className="text-warning font-medium">
                {formatCurrency(Math.abs(card.dailySpend), 2)}
              </span>
            </div>
          </div>
        )}

        {/* Pay Statement Balance */}
        {stmtBalance > 0 && onPayStatement && (
          <div className="mt-3 pt-3 border-t border-border">
            {!payMode ? (
              <button
                onClick={() => setPayMode(true)}
                className="w-full text-xs font-medium text-accent hover:text-accent/80 transition-colors py-1"
              >
                Pay Statement — {formatCurrency(stmtBalance)}
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-text-secondary">Pay {formatCurrency(stmtBalance)} from:</p>
                <select
                  value={payAccountId}
                  onChange={(e) => setPayAccountId(e.target.value)}
                  className="w-full text-xs bg-bg-tertiary border border-border rounded-lg px-2 py-1.5 text-text-primary focus:outline-none focus:border-accent"
                >
                  <option value="">Select account...</option>
                  {(accounts || []).map((a) => (
                    <option key={a.id} value={a.id}>{a.name} ({formatCurrency(a.balance)})</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={handlePayStatement}
                    disabled={!payAccountId}
                    className="flex-1 text-xs font-medium text-white bg-accent rounded-lg py-1.5 hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => { setPayMode(false); setPayAccountId(''); }}
                    className="flex-1 text-xs font-medium text-text-secondary bg-bg-tertiary border border-border rounded-lg py-1.5 hover:bg-bg-secondary transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function MasterAccounts() {
  const accounts = useStore((s) => s.accounts);
  const creditCards = useStore((s) => s.creditCards);
  const addAccount = useStore((s) => s.addAccount);
  const updateAccount = useStore((s) => s.updateAccount);
  const removeAccount = useStore((s) => s.removeAccount);
  const addCreditCard = useStore((s) => s.addCreditCard);
  const updateCreditCard = useStore((s) => s.updateCreditCard);
  const removeCreditCard = useStore((s) => s.removeCreditCard);
  const addNetWorthSnapshot = useStore((s) => s.addNetWorthSnapshot);
  const addTransaction = useStore((s) => s.addTransaction);
  const investments = useStore((s) => s.investments);
  const balanceSheetMonthly = useStore((s) => s.balanceSheetMonthly);
  const upsertBalanceSheetMonth = useStore((s) => s.upsertBalanceSheetMonth);
  const userSettings = useStore((s) => s.userSettings);
  const updateUserSettings = useStore((s) => s.updateUserSettings);

  const [showHidden, setShowHidden] = useState(false);

  const hiddenAccounts = userSettings.hiddenAccounts || [];
  const hiddenCreditCards = userSettings.hiddenCreditCards || [];
  const excludeFromLiquidity = userSettings.excludeFromLiquidity || [];

  const toggleHiddenAccount = (id) => {
    const next = hiddenAccounts.includes(id)
      ? hiddenAccounts.filter((x) => x !== id)
      : [...hiddenAccounts, id];
    updateUserSettings({ hiddenAccounts: next });
  };

  const toggleHiddenCreditCard = (id) => {
    const next = hiddenCreditCards.includes(id)
      ? hiddenCreditCards.filter((x) => x !== id)
      : [...hiddenCreditCards, id];
    updateUserSettings({ hiddenCreditCards: next });
  };

  const toggleLiquidityExclusion = (id) => {
    const next = excludeFromLiquidity.includes(id)
      ? excludeFromLiquidity.filter((x) => x !== id)
      : [...excludeFromLiquidity, id];
    updateUserSettings({ excludeFromLiquidity: next });
  };

  const handlePayStatement = async (card, stmtAmount, source) => {
    // Update CC: reduce balance by statement amount, clear statement
    await updateCreditCard(card.id, {
      balance: (card.balance || 0) + stmtAmount,
      statement: 0,
    });
    // Deduct from source account
    if (source.sourceType === 'creditCard') {
      await updateCreditCard(source.id, { balance: (source.balance || 0) - stmtAmount });
    } else {
      await updateAccount(source.id, { balance: (source.balance || 0) - stmtAmount });
    }
    // Create CC Payment transaction
    await addTransaction({
      date: new Date().toLocaleDateString('sv-SE'),
      description: `CC Payment — ${card.name}`,
      amount: stmtAmount,
      category: 'CC Payment',
      account: source.name,
      type: 'expense',
    });
  };

  const [snapshotSaved, setSnapshotSaved] = useState(false);

  // ─── Global "As Of" date (persisted to Supabase) ───────────────────────
  const todayLocal = new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD in local tz
  const asOfDate = userSettings.accountsAsOfDate || todayLocal;
  const [editingAsOf, setEditingAsOf] = useState(false);
  const asOfRef = useRef(null);
  useEffect(() => {
    if (editingAsOf && asOfRef.current) asOfRef.current.focus();
  }, [editingAsOf]);
  const saveAsOfDate = (val) => {
    if (val) {
      updateUserSettings({ accountsAsOfDate: val });
      setEditingAsOf(false);
    }
  };

  // Staleness: days since last update
  const daysSinceUpdate = useMemo(() => {
    const asOf = new Date(asOfDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.floor((today - asOf) / (1000 * 60 * 60 * 24));
  }, [asOfDate]);

  // Contribution form state (must be declared before handleSaveSnapshot which references it)
  const [contribForm, setContribForm] = useState({
    contribRothIra: '',
    contribHsa: '',
    contrib401k: '',
    contribBrokerage: '',
  });

  // Save snapshot to BS Monthly (includes contributions)
  const handleSaveSnapshot = useCallback(async () => {
    const LOAN = ['loan'];
    const assets = accounts.reduce((sum, a) => LOAN.includes(a.type) ? sum : sum + a.balance, 0);
    const loanTotal = accounts.filter((a) => LOAN.includes(a.type)).reduce((sum, a) => sum + Math.abs(a.balance), 0);
    const ccTotal = creditCards.reduce((sum, c) => sum + Math.abs(c.balance), 0);
    const liabilities = ccTotal + loanTotal;
    await addNetWorthSnapshot({
      date: todayLocal,
      totalAssets: Math.round(assets * 100) / 100,
      totalLiabilities: Math.round(liabilities * 100) / 100,
      netWorth: Math.round((assets - liabilities) * 100) / 100,
    });
    // Upsert contributions to balance_sheet_monthly for current month
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    await upsertBalanceSheetMonth({
      month: monthKey,
      contribRothIra: parseFloat(contribForm.contribRothIra) || 0,
      contribHsa: parseFloat(contribForm.contribHsa) || 0,
      contrib401k: parseFloat(contribForm.contrib401k) || 0,
      contribBrokerage: parseFloat(contribForm.contribBrokerage) || 0,
      creditScore: userSettings?.creditScore || 0,
    });
    await updateUserSettings({ accountsAsOfDate: todayLocal });
    // Update as-of date on all credit cards
    await Promise.all(creditCards.map((c) => updateCreditCard(c.id, { asOfDate: todayLocal })));
    setSnapshotSaved(true);
    setTimeout(() => setSnapshotSaved(false), 3000);
  }, [accounts, creditCards, addNetWorthSnapshot, updateUserSettings, updateCreditCard, todayLocal, upsertBalanceSheetMonth, contribForm, userSettings?.creditScore]);

  // ─── Contributions ─────────────────────────────────────────────────────
  const CONTRIB_ACCOUNTS = [
    { key: 'contribRothIra', label: 'IRA', limit: 7500 },
    { key: 'contribHsa', label: 'HSA', limit: 4300 },
    { key: 'contrib401k', label: '401(k)', limit: 23500 },
    { key: 'contribBrokerage', label: 'Brokerage', limit: null },
  ];

  const currentMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const currentYear = new Date().getFullYear();

  // Current month's contribution values (from balance_sheet_monthly if already saved)
  const currentMonthBS = useMemo(
    () => balanceSheetMonthly.find((r) => r.month === currentMonthKey) || {},
    [balanceSheetMonthly, currentMonthKey],
  );

  // Initialize form from stored values when they load
  useEffect(() => {
    const bs = balanceSheetMonthly.find((r) => r.month === currentMonthKey);
    if (bs) {
      setContribForm({
        contribRothIra: bs.contribRothIra || '',
        contribHsa: bs.contribHsa || '',
        contrib401k: bs.contrib401k || '',
        contribBrokerage: bs.contribBrokerage || '',
      });
    }
  }, [balanceSheetMonthly, currentMonthKey]);

  const saveContribField = (key, val) => {
    const num = parseFloat(val) || 0;
    setContribForm((prev) => ({ ...prev, [key]: num || '' }));
    upsertBalanceSheetMonth({ month: currentMonthKey, [key]: num });
  };

  // YTD contributions (sum all months in current year, merging Supabase + historical seed)
  const ytdContributions = useMemo(() => {
    const ytd = computeYtdContributions(balanceSheetMonthly, currentYear);

    // For current month: use form values if no DB record yet
    const hasDbRecord = balanceSheetMonthly.some((r) => r.month === currentMonthKey);
    if (!hasDbRecord) {
      CONTRIB_ACCOUNTS.forEach(({ key }) => {
        ytd[key] += parseFloat(contribForm[key]) || 0;
      });
      ytd.total = CONTRIB_ACCOUNTS.reduce((s, { key }) => s + ytd[key], 0);
    }

    return ytd;
  }, [balanceSheetMonthly, currentMonthKey, currentYear, contribForm]);

  // Investment performance: per-account and total
  const TAX_ADV_KEYWORDS = ['401k', '401(k)', 'ira', 'hsa', 'roth', 'sep', '403b', '457'];
  const isTaxAdvantage = (name) => {
    const lower = (name || '').toLowerCase();
    return TAX_ADV_KEYWORDS.some((k) => lower.includes(k));
  };

  const performance = useMemo(() => {
    // Dec of prior year (starting balances) and 12 months ago (YoY)
    const decKey = `${currentYear - 1}-12`;
    const decBS = balanceSheetMonthly.find((r) => r.month === decKey) || {};
    const now = new Date();
    const yoyMonth = `${now.getFullYear() - 1}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const yoyBS = balanceSheetMonthly.find((r) => r.month === yoyMonth) || {};

    // Previous month BS for MoM
    const prevM = now.getMonth() === 0 ? 12 : now.getMonth(); // 1-based prev month
    const prevY = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const prevMonthKey = `${prevY}-${String(prevM).padStart(2, '0')}`;
    const prevBS = balanceSheetMonthly.find((r) => r.month === prevMonthKey) || {};

    // Current live balances from investments
    const holdingsByAccount = {};
    investments.forEach((inv) => {
      const acct = inv.account || 'Unassigned';
      if (!holdingsByAccount[acct]) holdingsByAccount[acct] = 0;
      holdingsByAccount[acct] += inv.currentValue || 0;
    });

    // Map account display names to BS keys
    const accountMap = [
      { label: 'IRA', bsKey: 'rothIra', contribKey: 'contribRothIra', currentFn: () => {
        return Object.entries(holdingsByAccount)
          .filter(([name]) => /roth\s*ira/i.test(name) && !/401/i.test(name))
          .reduce((s, [, v]) => s + v, 0);
      }},
      { label: 'HSA', bsKey: 'hsa', contribKey: 'contribHsa', currentFn: () => {
        return Object.entries(holdingsByAccount)
          .filter(([name]) => /hsa/i.test(name))
          .reduce((s, [, v]) => s + v, 0);
      }},
      { label: '401(k)', bsKey: 'roth401k', contribKey: 'contrib401k', currentFn: () => {
        return Object.entries(holdingsByAccount)
          .filter(([name]) => /401k|401\(k\)/i.test(name))
          .reduce((s, [, v]) => s + v, 0);
      }},
      { label: 'Brokerage', bsKey: 'robinhood', contribKey: 'contribBrokerage', currentFn: () => {
        return Object.entries(holdingsByAccount)
          .filter(([name]) => !isTaxAdvantage(name))
          .reduce((s, [, v]) => s + v, 0);
      }},
    ];

    let totalStarting = 0;
    let totalCurrent = 0;
    let totalContrib = 0;
    let totalYoy = 0;
    let totalPrev = 0;

    const rows = accountMap.map(({ label, bsKey, contribKey, currentFn }) => {
      const starting = decBS[bsKey] || 0;
      const current = currentFn();
      const ytdContrib = ytdContributions[contribKey] || 0;
      const gain = current - starting - ytdContrib;
      const pct = starting > 0 ? (gain / starting) * 100 : 0;
      const yoyBase = yoyBS[bsKey] || 0;
      const yoyPct = yoyBase > 0 ? ((current - yoyBase) / yoyBase) * 100 : null;
      const prevBal = prevBS[bsKey] || 0;
      const momChange = current - prevBal;
      const momPct = prevBal > 0 ? (momChange / prevBal) * 100 : null;

      totalStarting += starting;
      totalCurrent += current;
      totalContrib += ytdContrib;
      totalYoy += yoyBase;
      totalPrev += prevBal;

      return { label, starting, current, ytdContrib, gain, pct, yoyPct, prevBal, momChange, momPct };
    });

    const totalGain = totalCurrent - totalStarting - totalContrib;
    const totalPct = totalStarting > 0 ? (totalGain / totalStarting) * 100 : 0;
    const totalYoyPct = totalYoy > 0 ? ((totalCurrent - totalYoy) / totalYoy) * 100 : null;
    const totalMomChange = totalCurrent - totalPrev;
    const totalMomPct = totalPrev > 0 ? (totalMomChange / totalPrev) * 100 : null;

    return {
      rows,
      total: { label: 'Total Portfolio', starting: totalStarting, current: totalCurrent, ytdContrib: totalContrib, gain: totalGain, pct: totalPct, yoyPct: totalYoyPct, prevBal: totalPrev, momChange: totalMomChange, momPct: totalMomPct },
      prevMonthLabel: new Date(prevY, prevM - 1).toLocaleDateString('en-US', { month: 'short' }),
    };
  }, [balanceSheetMonthly, investments, ytdContributions, currentYear]);

  // ─── Form visibility state ──────────────────────────────────────────────
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);

  // ─── Form data state ────────────────────────────────────────────────────
  const [accountForm, setAccountForm] = useState(BLANK_ACCOUNT);
  const [creditCardForm, setCreditCardForm] = useState(BLANK_CREDIT_CARD);

  // ─── Form handlers ──────────────────────────────────────────────────────
  const handleAccountChange = (field, value) => {
    setAccountForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-suggest BS category when type or name changes (only if not manually set)
      if ((field === 'type' || field === 'name') && !prev._manualCategory) {
        updated.balanceSheetCategory = inferBalanceSheetCategory(updated) || '';
      }
      // Track if user manually chose a category
      if (field === 'balanceSheetCategory') {
        updated._manualCategory = !!value;
      }
      return updated;
    });
  };

  const handleCreditCardChange = (field, value) => {
    setCreditCardForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAccount = (e) => {
    e.preventDefault();
    if (!accountForm.name.trim()) return;
    const payload = {
      name: accountForm.name.trim(),
      type: accountForm.type,
      balance: parseNumericInput(accountForm.balance) || 0,
      institution: accountForm.institution.trim(),
      statement: parseNumericInput(accountForm.statement) || 0,
      asOfDate: accountForm.asOfDate || null,
    };
    // Include explicit category, or auto-infer, or null
    payload.balanceSheetCategory = accountForm.balanceSheetCategory || inferBalanceSheetCategory(payload) || null;
    addAccount(payload);
    setAccountForm(BLANK_ACCOUNT);
    setShowAccountForm(false);
  };

  const handleAddCreditCard = (e) => {
    e.preventDefault();
    if (!creditCardForm.name.trim()) return;
    addCreditCard({
      name: creditCardForm.name.trim(),
      lastFour: creditCardForm.lastFour.trim(),
      type: creditCardForm.type,
      balance: parseNumericInput(creditCardForm.balance) || 0,
      creditLimit: parseNumericInput(creditCardForm.creditLimit) || 0,
      annualFee: parseNumericInput(creditCardForm.annualFee) || 0,
      interestRate: parseFloat(creditCardForm.interestRate) || 0,
      pointsBalance: parseNumericInput(creditCardForm.pointsBalance) || 0,
      pointsType: creditCardForm.pointsType.trim(),
      paymentDueDate: creditCardForm.paymentDueDate || null,
      lastClosingDate: creditCardForm.lastClosingDate || null,
      nextClosingDate: creditCardForm.nextClosingDate || null,
      statement: parseNumericInput(creditCardForm.statement) || 0,
      asOfDate: creditCardForm.asOfDate || null,
    });
    setCreditCardForm(BLANK_CREDIT_CARD);
    setShowCreditCardForm(false);
  };

  // ─── Computed values ────────────────────────────────────────────────────
  const {
    cashAccounts,
    investmentAccounts,
    propertyAccounts,
    otherAccounts,
    loanAccounts,
    allCashAccounts,
    allInvestmentAccounts,
    allPropertyAccounts,
    allOther,
    allLoans,
    totalAssets,
    totalLiabilities,
    netWorth,
    ccUtilization,
    cashStatementTotal,
    ccPaymentsDue,
    nwc,
  } = useMemo(() => {
    const visFilter = (a) => showHidden || !hiddenAccounts.includes(a.id);
    // Auto-compute loyalty points value from total credit card points/miles × $0.01
    const loyaltyPointsValue = creditCards.reduce((sum, c) => sum + (c.pointsBalance || 0), 0) * 0.01;
    const applyLoyalty = (a) => /est\.?\s*loyalty/i.test(a.name) ? { ...a, balance: loyaltyPointsValue } : a;

    // Visible lists (for rendering sections)
    const cashAccounts = accounts.filter((a) => CASH_TYPES.includes(a.type) && visFilter(a));
    const investmentAccounts = accounts.filter((a) => INVESTMENT_TYPES.includes(a.type) && visFilter(a));
    const propertyAccounts = accounts.filter((a) => PROPERTY_TYPES.includes(a.type) && visFilter(a));
    const otherAccounts = accounts.filter((a) => OTHER_TYPES.includes(a.type) && visFilter(a)).map(applyLoyalty);
    const loanAccounts = accounts.filter((a) => LOAN_TYPES.includes(a.type) && visFilter(a));

    // Unfiltered lists for totals (always include ALL accounts, hidden or not)
    const allCashAccounts = accounts.filter((a) => CASH_TYPES.includes(a.type));
    const allInvestmentAccounts = accounts.filter((a) => INVESTMENT_TYPES.includes(a.type));
    const allPropertyAccounts = accounts.filter((a) => PROPERTY_TYPES.includes(a.type));
    const allOther = accounts.filter((a) => OTHER_TYPES.includes(a.type)).map(applyLoyalty);
    const allLoans = accounts.filter((a) => LOAN_TYPES.includes(a.type));

    const totalAssets = accounts.reduce((sum, a) => {
      if (LOAN_TYPES.includes(a.type)) return sum;
      const bal = (a.type === 'other' && /est\.?\s*loyalty/i.test(a.name)) ? loyaltyPointsValue : a.balance;
      return sum + bal;
    }, 0);

    const loanTotal = allLoans.reduce((sum, a) => sum + Math.abs(a.balance), 0);
    const ccTotal = creditCards.reduce((sum, c) => sum + Math.abs(c.balance), 0);
    const totalLiabilities = ccTotal + loanTotal;

    const totalLimit = creditCards.reduce((sum, c) => sum + c.creditLimit, 0);
    const ccUtilization = totalLimit > 0 ? (ccTotal / totalLimit) * 100 : 0;

    const netWorth = totalAssets - totalLiabilities;

    // NWC: cash statement balances (incl. taxable brokerage + accounts receivable) less CC payments due
    // Always uses all accounts regardless of hidden status
    const brokerageAccounts = accounts.filter((a) => a.type === 'investment' && /brokerage/i.test(a.name));
    const receivableAccounts = allOther.filter((a) => /receivable/i.test(a.name));
    const cashStatementTotal = [...allCashAccounts, ...brokerageAccounts, ...receivableAccounts]
      .filter((a) => !excludeFromLiquidity.includes(a.id))
      .reduce((sum, a) => {
        const val = (a.statement != null && a.statement !== 0) ? a.statement : a.balance;
        return sum + val;
      }, 0);
    const ccPaymentsDue = creditCards.reduce((sum, c) => {
      return sum + Math.abs(c.statement || 0);
    }, 0);
    const nwc = cashStatementTotal - ccPaymentsDue;

    return {
      cashAccounts,
      investmentAccounts,
      propertyAccounts,
      otherAccounts,
      loanAccounts,
      allCashAccounts,
      allInvestmentAccounts,
      allPropertyAccounts,
      allOther,
      allLoans,
      totalAssets,
      totalLiabilities,
      netWorth,
      ccUtilization,
      cashStatementTotal,
      ccPaymentsDue,
      nwc,
    };
  }, [accounts, creditCards, showHidden, hiddenAccounts, excludeFromLiquidity]);

  // ─── Points & Miles summary ─────────────────────────────────────────────
  const { pointsSummary, totalPoints } = useMemo(() => {
    const pointsMap = {};
    creditCards.forEach((c) => {
      if (c.pointsType) {
        if (!pointsMap[c.pointsType]) {
          pointsMap[c.pointsType] = 0;
        }
        pointsMap[c.pointsType] += (c.pointsBalance || 0);
      }
    });

    const pointsSummary = Object.entries(pointsMap).map(([type, points]) => ({
      type,
      points,
    }));

    const totalPoints = pointsSummary.reduce((sum, p) => sum + p.points, 0);

    return { pointsSummary, totalPoints };
  }, [creditCards]);

  // ─── Upcoming payments ──────────────────────────────────────────────────
  const upcomingPayments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Advance a due date to the next upcoming occurrence (same day-of-month)
    const nextDueDate = (dateStr) => {
      const orig = new Date(dateStr + 'T00:00:00');
      const day = orig.getDate();
      // Start from current month
      let d = new Date(today.getFullYear(), today.getMonth(), day);
      // If that date is in the past, move to next month
      if (d < today) d.setMonth(d.getMonth() + 1);
      return d;
    };

    return creditCards
      .filter((c) => c.paymentDueDate)
      .map((c) => ({ ...c, nextDueDate: nextDueDate(c.paymentDueDate) }))
      .sort((a, b) => a.nextDueDate - b.nextDueDate);
  }, [creditCards]);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Master Accounts"
        subtitle={
          <>
            {accounts.length} accounts &middot; {creditCards.length} cards &middot; As of{' '}
            {editingAsOf ? (
              <input
                ref={asOfRef}
                type="date"
                value={asOfDate}
                onChange={(e) => saveAsOfDate(e.target.value)}
                onBlur={() => setTimeout(() => setEditingAsOf(false), 200)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveAsOfDate(e.target.value); if (e.key === 'Escape') setEditingAsOf(false); }}
                className="bg-bg-tertiary border border-accent rounded px-1.5 py-0.5 text-xs text-text-primary focus:outline-none"
              />
            ) : (
              <span
                className={`cursor-pointer hover:text-accent transition-colors underline decoration-dotted underline-offset-2 ${
                  daysSinceUpdate > 30 ? 'text-danger' : daysSinceUpdate > 7 ? 'text-warning' : ''
                }`}
                onClick={() => setEditingAsOf(true)}
                title={daysSinceUpdate > 0 ? `${daysSinceUpdate} day${daysSinceUpdate !== 1 ? 's' : ''} ago — click to update` : 'Click to edit'}
              >
                {formatDate(asOfDate)}
              </span>
            )}
          </>
        }
      >
        <button
          onClick={() => setShowHidden((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            showHidden
              ? 'bg-accent/20 text-accent'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
          title={showHidden ? 'Hide hidden accounts' : 'Show hidden accounts'}
        >
          {showHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {showHidden ? 'Showing hidden' : 'Hidden'}
        </button>
        <button
          onClick={handleSaveSnapshot}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            snapshotSaved
              ? 'bg-success/20 text-success'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
          title="Save current totals as a balance sheet snapshot"
        >
          <Camera className="w-3.5 h-3.5" />
          {snapshotSaved ? 'Saved!' : 'Snapshot'}
        </button>
<button
          onClick={() => setShowAccountForm((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent/90 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Account
        </button>
        <button
          onClick={() => setShowCreditCardForm((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent/90 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Card
        </button>
      </PageHeader>

{/* ── Add Account Form ──────────────────────────────────────────── */}
      {showAccountForm && (
        <Card className="section-gap">
          <form onSubmit={handleAddAccount}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-text-primary">
                Add Account
              </h3>
              <button
                type="button"
                onClick={() => setShowAccountForm(false)}
                className="p-1 text-text-muted hover:text-text-primary transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Name *</label>
                <input
                  type="text"
                  placeholder="Account name"
                  value={accountForm.name}
                  onChange={(e) => handleAccountChange('name', e.target.value)}
                  className={INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Type</label>
                <select
                  value={accountForm.type}
                  onChange={(e) => handleAccountChange('type', e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="cash">Cash</option>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                  <option value="investment">Investment</option>
                  <option value="property">Property</option>
                  <option value="loan">Loan / Mortgage</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Balance</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={accountForm.balance}
                  onChange={(e) => handleAccountChange('balance', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Institution</label>
                <input
                  type="text"
                  placeholder="Bank or institution"
                  value={accountForm.institution}
                  onChange={(e) => handleAccountChange('institution', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Liquidity</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Current / liquid"
                  value={accountForm.statement}
                  onChange={(e) => handleAccountChange('statement', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">As Of Date</label>
                <input
                  type="date"
                  value={accountForm.asOfDate}
                  onChange={(e) => handleAccountChange('asOfDate', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">BS Category</label>
                <select
                  value={accountForm.balanceSheetCategory}
                  onChange={(e) => handleAccountChange('balanceSheetCategory', e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="">(Auto-detect)</option>
                  {BS_CATEGORY_OPTIONS.map((group) => (
                    <optgroup key={group.group} label={group.group}>
                      {group.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAccountForm(BLANK_ACCOUNT);
                  setShowAccountForm(false);
                }}
                className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent/90 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Account
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* ── Add Credit Card Form ──────────────────────────────────────── */}
      {showCreditCardForm && (
        <Card className="section-gap">
          <form onSubmit={handleAddCreditCard}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-text-primary">
                Add Credit Card
              </h3>
              <button
                type="button"
                onClick={() => setShowCreditCardForm(false)}
                className="p-1 text-text-muted hover:text-text-primary transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Name *</label>
                <input
                  type="text"
                  placeholder="Card name"
                  value={creditCardForm.name}
                  onChange={(e) => handleCreditCardChange('name', e.target.value)}
                  className={INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Last Four</label>
                <input
                  type="text"
                  maxLength={4}
                  placeholder="1234"
                  value={creditCardForm.lastFour}
                  onChange={(e) => handleCreditCardChange('lastFour', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Type</label>
                <select
                  value={creditCardForm.type}
                  onChange={(e) => handleCreditCardChange('type', e.target.value)}
                  className={INPUT_CLASS}
                >
                  <option value="Visa">Visa</option>
                  <option value="Mastercard">Mastercard</option>
                  <option value="AMEX">AMEX</option>
                  <option value="Discover">Discover</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Balance</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={creditCardForm.balance}
                  onChange={(e) => handleCreditCardChange('balance', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Credit Limit</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={creditCardForm.creditLimit}
                  onChange={(e) => handleCreditCardChange('creditLimit', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Annual Fee</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={creditCardForm.annualFee}
                  onChange={(e) => handleCreditCardChange('annualFee', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Interest Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={creditCardForm.interestRate}
                  onChange={(e) => handleCreditCardChange('interestRate', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Points Balance</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={creditCardForm.pointsBalance}
                  onChange={(e) => handleCreditCardChange('pointsBalance', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Points Type</label>
                <input
                  type="text"
                  placeholder="e.g. Chase UR"
                  value={creditCardForm.pointsType}
                  onChange={(e) => handleCreditCardChange('pointsType', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Payment Due Date</label>
                <input
                  type="date"
                  value={creditCardForm.paymentDueDate}
                  onChange={(e) => handleCreditCardChange('paymentDueDate', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Last Closing Date</label>
                <input
                  type="date"
                  value={creditCardForm.lastClosingDate}
                  onChange={(e) => handleCreditCardChange('lastClosingDate', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Next Closing Date</label>
                <input
                  type="date"
                  value={creditCardForm.nextClosingDate}
                  onChange={(e) => handleCreditCardChange('nextClosingDate', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Statement Balance</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={creditCardForm.statement}
                  onChange={(e) => handleCreditCardChange('statement', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">As Of Date</label>
                <input
                  type="date"
                  value={creditCardForm.asOfDate}
                  onChange={(e) => handleCreditCardChange('asOfDate', e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCreditCardForm(BLANK_CREDIT_CARD);
                  setShowCreditCardForm(false);
                }}
                className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent/90 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Credit Card
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* ── KPI Cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
        <KPICard
          title="Net Worth"
          value={formatCurrency(netWorth)}
          icon={Landmark}
        />
        <KPICard
          title="Total Assets"
          value={formatCurrency(totalAssets)}
          icon={DollarSign}
        />
        <KPICard
          title="Total Liabilities"
          value={formatCurrency(totalLiabilities)}
          icon={TrendingDown}
          variant="inverse"
        />
        <KPICard
          title="CC Utilization"
          value={`${ccUtilization.toFixed(1)}%`}
          icon={Gauge}
        />
      </div>

      {/* ── Credit Score ───────────────────────────────────────────────── */}
      <Card className="section-gap">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">Credit Score</h3>
          <div className="flex items-center gap-3">
            <InlineEditText
              value={userSettings.creditScore || 0}
              displayValue={
                userSettings.creditScore
                  ? String(userSettings.creditScore)
                  : '—'
              }
              className="text-2xl font-bold text-text-primary"
              inputClassName="w-20 text-xl font-bold text-text-primary text-right"
              onSave={(val) => updateUserSettings({ creditScore: Math.round(val) })}
            />
            <span className="text-xs text-text-muted">Chase</span>
          </div>
        </div>
      </Card>

      {/* ── Net Working Capital ────────────────────────────────────────── */}
      <Card className="section-gap">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-text-primary">
            Net Working Capital (NWC)
          </h3>
          <span className={`text-lg font-bold ${nwc >= 0 ? 'text-success' : 'text-danger'}`}>
            {formatCurrency(nwc)}
          </span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Liquidity</span>
            <span className="font-mono text-text-primary">{formatCurrency(cashStatementTotal)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-secondary">Less: CC Payments Due</span>
            <span className="font-mono text-danger">−{formatCurrency(ccPaymentsDue)}</span>
          </div>
          <div className="border-t border-border pt-2 flex items-center justify-between font-semibold">
            <span className="text-text-primary">NWC</span>
            <span className={`font-mono ${nwc >= 0 ? 'text-success' : 'text-danger'}`}>
              {formatCurrency(nwc)}
            </span>
          </div>
        </div>
      </Card>

      {/* ── Account Sections ──────────────────────────────────────────── */}
      <AccountSection title="Cash & Equivalents" accounts={cashAccounts} allForTotal={allCashAccounts} onRemove={removeAccount} onUpdate={updateAccount} onToggleHidden={toggleHiddenAccount} onToggleLiquidity={toggleLiquidityExclusion} hiddenIds={hiddenAccounts} liquidityExclusions={excludeFromLiquidity} liquidityEligible={() => true} />
      <AccountSection title="Investments" accounts={investmentAccounts} allForTotal={allInvestmentAccounts} onRemove={removeAccount} onUpdate={updateAccount} onToggleHidden={toggleHiddenAccount} onToggleLiquidity={toggleLiquidityExclusion} hiddenIds={hiddenAccounts} liquidityExclusions={excludeFromLiquidity} editableFilter={() => false} liquidityEligible={(a) => /brokerage/i.test(a.name)} />
      <AccountSection title="Property" accounts={propertyAccounts} allForTotal={allPropertyAccounts} onRemove={removeAccount} onUpdate={updateAccount} onToggleHidden={toggleHiddenAccount} hiddenIds={hiddenAccounts} />
      <AccountSection
        title="Other Assets"
        accounts={otherAccounts}
        allForTotal={allOther}
        onRemove={removeAccount}
        onUpdate={updateAccount}
        onToggleHidden={toggleHiddenAccount}
        onToggleLiquidity={toggleLiquidityExclusion}
        hiddenIds={hiddenAccounts}
        liquidityExclusions={excludeFromLiquidity}
        editableFilter={(a) => !/est\.?\s*loyalty/i.test(a.name) && a.balanceSheetCategory !== 'ownershiftEquity'}
        liquidityEligible={(a) => /receivable/i.test(a.name)}
      />
      <AccountSection title="Loans & Liabilities" accounts={loanAccounts} allForTotal={allLoans} onRemove={removeAccount} onUpdate={updateAccount} onToggleHidden={toggleHiddenAccount} hiddenIds={hiddenAccounts} />

      {/* ── Contributions & Performance ────────────────────────────────── */}
      <Card className="section-gap">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <PiggyBank className="w-4 h-4 text-accent" />
            Contributions & Performance
          </h3>
          <span className="text-xs text-text-muted">{currentYear} YTD</span>
        </div>

        {/* Monthly Contribution Inputs */}
        <div className="mb-5">
          <p className="text-xs text-text-muted mb-2">
            This month's contributions ({currentMonthKey})
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CONTRIB_ACCOUNTS.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-[10px] text-text-muted uppercase tracking-wider mb-1">
                  {label}
                </label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={contribForm[key]}
                  onChange={(e) => setContribForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  onBlur={(e) => saveContribField(key, e.target.value)}
                  className="w-full bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm text-text-primary font-mono text-right focus:outline-none focus:border-accent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            ))}
          </div>
        </div>

        {/* YTD Progress Bars */}
        <div className="mb-5">
          <p className="text-xs text-text-muted mb-2">YTD Progress</p>
          <div className="space-y-2">
            {CONTRIB_ACCOUNTS.map(({ key, label, limit }) => {
              const ytd = ytdContributions[key];
              const pct = limit ? Math.min(100, (ytd / limit) * 100) : 0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-xs text-text-secondary w-20 shrink-0">{label}</span>
                  <div className="flex-1 h-2 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-success' : 'bg-accent'}`}
                      style={{ width: limit ? `${pct}%` : '0%' }}
                    />
                  </div>
                  <span className="text-xs font-mono text-text-primary w-24 text-right">
                    {formatCurrency(ytd)}
                  </span>
                  {limit && (
                    <span className="text-xs text-text-muted w-24 text-right">
                      / {formatCurrency(limit)}
                    </span>
                  )}
                  {!limit && (
                    <span className="text-xs text-text-muted w-24 text-right">no limit</span>
                  )}
                </div>
              );
            })}
            <div className="flex items-center gap-3 pt-1 border-t border-border/30">
              <span className="text-xs font-semibold text-text-primary w-20 shrink-0">Total</span>
              <div className="flex-1" />
              <span className="text-xs font-mono font-semibold text-text-primary w-24 text-right">
                {formatCurrency(ytdContributions.total)}
              </span>
              <span className="w-24" />
            </div>
          </div>
        </div>

        {/* Performance Table */}
        <div>
          <p className="text-xs text-text-muted mb-2">Investment Performance</p>
          <Table>
            <Thead>
              <tr>
                <Th>Account</Th>
                <Th align="right">Jan 1 Balance</Th>
                <Th align="right">{performance.prevMonthLabel} Balance</Th>
                <Th align="right">Current</Th>
                <Th align="right">Contributions</Th>
                <Th align="right">Gain / (Loss)</Th>
                <Th align="right">MoM %</Th>
                <Th align="right">YTD %</Th>
                <Th align="right">YoY %</Th>
              </tr>
            </Thead>
            <tbody>
              {performance.rows.map((row) => (
                <Tr key={row.label}>
                  <Td className="text-text-primary font-medium">{row.label}</Td>
                  <Td align="right" className="font-mono text-text-secondary tabular-nums">
                    {formatCurrency(row.starting)}
                  </Td>
                  <Td align="right" className="font-mono text-text-secondary tabular-nums">
                    {formatCurrency(row.prevBal)}
                  </Td>
                  <Td align="right" className="font-mono text-text-primary tabular-nums">
                    {formatCurrency(row.current)}
                  </Td>
                  <Td align="right" className="font-mono text-text-secondary tabular-nums">
                    {formatCurrency(row.ytdContrib)}
                  </Td>
                  <Td align="right" className={`font-mono font-semibold tabular-nums ${row.gain >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatCurrency(row.gain)}
                  </Td>
                  <Td align="right" className={`font-mono font-semibold tabular-nums ${row.momPct != null && row.momPct >= 0 ? 'text-success' : 'text-danger'}`}>
                    {row.momPct != null ? `${row.momPct >= 0 ? '+' : ''}${row.momPct.toFixed(1)}%` : '—'}
                  </Td>
                  <Td align="right" className={`font-mono font-semibold tabular-nums ${row.pct >= 0 ? 'text-success' : 'text-danger'}`}>
                    {row.starting > 0 ? `${row.pct >= 0 ? '+' : ''}${row.pct.toFixed(1)}%` : '—'}
                  </Td>
                  <Td align="right" className={`font-mono font-semibold tabular-nums ${row.yoyPct != null && row.yoyPct >= 0 ? 'text-success' : 'text-danger'}`}>
                    {row.yoyPct != null ? `${row.yoyPct >= 0 ? '+' : ''}${row.yoyPct.toFixed(1)}%` : '—'}
                  </Td>
                </Tr>
              ))}
              <tr className="border-t-2 border-border bg-bg-tertiary/30">
                <Td className="font-bold text-text-primary">{performance.total.label}</Td>
                <Td align="right" className="font-mono font-bold text-text-secondary tabular-nums">
                  {formatCurrency(performance.total.starting)}
                </Td>
                <Td align="right" className="font-mono font-bold text-text-secondary tabular-nums">
                  {formatCurrency(performance.total.prevBal)}
                </Td>
                <Td align="right" className="font-mono font-bold text-text-primary tabular-nums">
                  {formatCurrency(performance.total.current)}
                </Td>
                <Td align="right" className="font-mono font-bold text-text-secondary tabular-nums">
                  {formatCurrency(performance.total.ytdContrib)}
                </Td>
                <Td align="right" className={`font-mono font-bold tabular-nums ${performance.total.gain >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatCurrency(performance.total.gain)}
                </Td>
                <Td align="right" className={`font-mono font-bold tabular-nums ${performance.total.momPct != null && performance.total.momPct >= 0 ? 'text-success' : 'text-danger'}`}>
                  {performance.total.momPct != null ? `${performance.total.momPct >= 0 ? '+' : ''}${performance.total.momPct.toFixed(1)}%` : '—'}
                </Td>
                <Td align="right" className={`font-mono font-bold tabular-nums ${performance.total.pct >= 0 ? 'text-success' : 'text-danger'}`}>
                  {performance.total.starting > 0 ? `${performance.total.pct >= 0 ? '+' : ''}${performance.total.pct.toFixed(1)}%` : '—'}
                </Td>
                <Td align="right" className={`font-mono font-bold tabular-nums ${performance.total.yoyPct != null && performance.total.yoyPct >= 0 ? 'text-success' : 'text-danger'}`}>
                  {performance.total.yoyPct != null ? `${performance.total.yoyPct >= 0 ? '+' : ''}${performance.total.yoyPct.toFixed(1)}%` : '—'}
                </Td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Card>

      {/* ── Credit Card Tiles ─────────────────────────────────────────── */}
      {(() => {
        const visibleCards = creditCards.filter((c) => showHidden || !hiddenCreditCards.includes(c.id));
        if (!visibleCards.length) return null;
        // Totals always include all cards (hidden or not)
        const totalOutstanding = creditCards.reduce((sum, c) => sum + Math.abs(c.balance), 0);
        const totalLimit = creditCards.reduce((sum, c) => sum + (c.creditLimit || 0), 0);
        return (
          <div className="section-gap">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-primary">
                Credit Cards
                <span className="text-text-secondary text-sm font-normal ml-2">
                  {visibleCards.length} cards
                </span>
              </h2>
              <div className="text-right">
                <span className="text-lg font-bold text-danger">
                  {formatCurrency(totalOutstanding, 2)}
                </span>
                <span className="text-text-muted text-xs ml-2">
                  / {formatCurrency(totalLimit)} limit
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visibleCards.map((card) => (
                <div key={card.id} className={hiddenCreditCards.includes(card.id) ? 'opacity-50' : ''}>
                  <CreditCardTile card={card} onRemove={removeCreditCard} onUpdate={updateCreditCard} onToggleHidden={toggleHiddenCreditCard} isHidden={hiddenCreditCards.includes(card.id)} accounts={accounts} creditCards={creditCards} onPayStatement={handlePayStatement} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Points & Miles Summary ────────────────────────────────────── */}
      {pointsSummary.length > 0 && (
        <Card className="section-gap">
          <h3 className="text-sm font-semibold text-text-primary mb-4">
            Points & Miles Summary
          </h3>
          <Table>
            <Thead>
              <tr>
                <Th>Rewards Program</Th>
                <Th align="right">Balance</Th>
                <Th>Cards</Th>
              </tr>
            </Thead>
            <tbody>
              {pointsSummary.map((ps) => {
                const cards = creditCards.filter(
                  (c) => c.pointsType === ps.type,
                );
                return (
                  <Tr key={ps.type}>
                    <Td className="text-text-primary font-medium">{ps.type}</Td>
                    <Td align="right" className="font-mono text-text-primary font-semibold">
                      {formatNumber(ps.points)}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        {cards.map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-1.5">
                            <Badge variant="default">{c.name}</Badge>
                            <InlineEditText
                              value={c.pointsBalance}
                              displayValue={formatNumber(c.pointsBalance)}
                              className="text-xs font-mono text-text-secondary"
                              inputClassName="w-24 text-xs text-right"
                              onSave={(val) => updateCreditCard(c.id, { pointsBalance: val })}
                            />
                          </span>
                        ))}
                      </div>
                    </Td>
                  </Tr>
                );
              })}
              {/* Total Row */}
              <tr className="border-t-2 border-border bg-bg-tertiary/30">
                <Td className="font-bold text-text-primary">Total</Td>
                <Td align="right" className="font-mono font-bold text-text-primary">
                  {formatNumber(totalPoints)}
                </Td>
                <Td />
              </tr>
              {/* Estimated Value Row */}
              <tr className="bg-bg-tertiary/20">
                <Td className="text-text-secondary text-xs italic">
                  Est. Value @ $0.01/point
                </Td>
                <Td align="right" className="font-mono text-success font-semibold">
                  {formatCurrency(totalPoints * 0.01, 2)}
                </Td>
                <Td className="text-text-muted text-xs italic">auto-calculated</Td>
              </tr>
            </tbody>
          </Table>
        </Card>
      )}

      {/* ── Upcoming Payments ─────────────────────────────────────────── */}
      {upcomingPayments.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-4">
            Upcoming Payments
          </h3>
          <Table>
            <Thead>
              <tr>
                <Th>Card</Th>
                <Th>Due Date</Th>
                <Th align="right">Statement</Th>
                <Th align="right">Total Balance</Th>
              </tr>
            </Thead>
            <tbody>
              {upcomingPayments.map((card) => (
                <Tr key={card.id}>
                  <Td>
                    <span className="text-text-primary font-medium">
                      {card.name}
                    </span>
                  </Td>
                  <Td className="text-text-primary">
                    {formatDate(card.nextDueDate)}
                  </Td>
                  <Td align="right" className="font-mono text-text-secondary">
                    {card.statement ? formatCurrency(Math.abs(card.statement), 2) : '—'}
                  </Td>
                  <Td align="right" className="font-mono text-text-primary">
                    {formatCurrency(Math.abs(card.balance), 2)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  );
}
